import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { Camera, Upload, FileText, ArrowRight, X, CheckCircle2, Plus } from 'lucide-react';

/* ── Mock superbill for demo ── */
const MOCK_SUPERBILL = {
  name: 'Superbill_Eleanor_Vance_03282026.pdf',
  desc: 'Office visit — E/M 99214, CBC 85025, CMP 80053',
  patient: 'Eleanor Vance',
  dos: '03/28/2026',
  codes: '99214, 85025, 80053',
};

/* ── Apple-style processing animation ── */
const ProcessingAnimation = ({ step, files }) => {
  const steps = [
    { label: 'Reading document', done: step > 0 },
    { label: 'Extracting patient data', done: step > 1 },
    { label: 'Validating payer rules', done: step > 2 },
    { label: 'Calculating risk score', done: step > 3 },
  ];
  return (
    <div className="flex flex-col items-center py-6">
      {/* Pulsing ring */}
      <div className="relative mb-6">
        <div className="h-16 w-16 rounded-full border-[3px] border-neutral-300 flex items-center justify-center"
          style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-neutral-800 to-black flex items-center justify-center">
            <svg className="h-5 w-5 text-white animate-spin" style={{ animationDuration: '3s' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          </div>
        </div>
        <div className="absolute inset-0 rounded-full border-[3px] border-neutral-500 opacity-30"
          style={{ animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
      </div>

      <p style={{ fontSize: 14, fontWeight: 500, color: '#111', marginBottom: 4 }}>
        RevQ is reading your superbill...
      </p>
      <p style={{ fontSize: 11, color: '#aaa', marginBottom: 16 }}>
        {files > 1 ? `Processing ${files} documents` : 'This usually takes 5–15 seconds'}
      </p>

      {/* Sequential step indicators */}
      <div className="w-full max-w-xs space-y-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2.5 transition-all" style={{ opacity: step >= i ? 1 : 0.3, transform: step >= i ? 'translateX(0)' : 'translateX(-8px)', transition: 'all 0.4s ease' }}>
            {s.done ? (
              <CheckCircle2 style={{ width: 14, height: 14, color: '#22c55e', flexShrink: 0 }} />
            ) : step === i ? (
              <div className="h-3.5 w-3.5 rounded-full border-2 border-neutral-800 border-t-transparent animate-spin" style={{ flexShrink: 0 }} />
            ) : (
              <div className="h-3.5 w-3.5 rounded-full border border-slate-300" style={{ flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 12, color: s.done ? '#22c55e' : step === i ? '#111' : '#aaa', fontWeight: step === i ? 500 : 400 }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LensUploadLightbox = ({ open, onOpenChange, onProcessed, navigateToBillingFocus = true }) => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processStep, setProcessStep] = useState(-1);

  useEffect(() => {
    if (open) { setFiles([]); setProcessing(false); setProcessStep(-1); }
  }, [open]);

  const addFiles = (newFiles) => {
    const arr = Array.from(newFiles);
    setFiles(prev => [...prev, ...arr.map(f => ({ name: f.name, size: f.size, type: f.type }))]);
  };

  const handleFilePick = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleUseMock = () => {
    setFiles([{ name: MOCK_SUPERBILL.name, size: 284000, type: 'application/pdf', mock: true }]);
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleProcess = async () => {
    setProcessing(true);
    // Apple-style sequential step animation
    for (let i = 0; i <= 4; i++) {
      setProcessStep(i);
      await new Promise(r => setTimeout(r, i === 0 ? 800 : i === 3 ? 600 : 1000));
    }
    setProcessing(false);
    onOpenChange(false);
    if (onProcessed) onProcessed(files);
    if (navigateToBillingFocus) navigate('/revq/billing-focus');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("p-0 gap-0 overflow-hidden", processing ? "max-w-md" : "max-w-lg")} data-testid="lens-upload-lightbox">
        <DialogHeader className="p-4 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="h-4 w-4" />
            Scan with Lens
            <span style={{ fontSize: 10, background: '#f0edff', color: '#5b21b6', borderRadius: 20, padding: '2px 8px', fontWeight: 500 }}>Echo-Powered</span>
          </DialogTitle>
        </DialogHeader>

        {processing ? (
          <div style={{ padding: '12px 24px 24px' }}>
            <ProcessingAnimation step={processStep} files={files.length} />
          </div>
        ) : (
          <div style={{ padding: '16px 24px' }}>
            {/* Drop zone */}
            <div
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className="flex flex-col items-center cursor-pointer transition-all"
              style={{
                border: dragActive ? '1.5px solid #111' : '1.5px dashed #d1d5db',
                borderRadius: 12, padding: '24px 20px',
                background: dragActive ? '#f5f3ff' : '#fafafa',
              }}
              data-testid="lens-drop-zone"
            >
              <Camera style={{ width: 24, height: 24, color: '#111' }} />
              <p style={{ fontSize: 13, fontWeight: 500, color: '#111', marginTop: 8 }}>
                Drop superbills or EOBs here
              </p>
              <p style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>
                Take a photo, upload files, or drag & drop — batch upload supported
              </p>
              <div className="flex gap-2 mt-3">
                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#f0f0f0', color: '#888' }}>PDF</span>
                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#f0f0f0', color: '#888' }}>JPG</span>
                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#f0f0f0', color: '#888' }}>PNG</span>
                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#f0f0f0', color: '#888' }}>CSV</span>
              </div>
              <input ref={fileRef} type="file" accept="image/*,.pdf,.csv" multiple className="hidden" onChange={handleFilePick} />
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase' }}>or try a sample</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Mock superbill */}
            <button
              onClick={handleUseMock}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-left"
              data-testid="lens-use-mock-btn"
            >
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f0edff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText style={{ width: 16, height: 16, color: '#111' }} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>{MOCK_SUPERBILL.name}</p>
                <p style={{ fontSize: 11, color: '#888' }}>{MOCK_SUPERBILL.desc}</p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
            </button>

            {/* Queued files */}
            {files.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p style={{ fontSize: 11, fontWeight: 500, color: '#555' }}>{files.length} file{files.length > 1 ? 's' : ''} queued</p>
                  <button onClick={() => fileRef.current?.click()} style={{ fontSize: 11, color: '#111', background: 'none', border: 'none', cursor: 'pointer' }} className="flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Add more
                  </button>
                </div>
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: '#f9fafb', border: '0.5px solid #e8e8e8' }}>
                    <FileText style={{ width: 14, height: 14, color: '#111' }} />
                    <span style={{ fontSize: 11, color: '#111', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                      <X style={{ width: 12, height: 12, color: '#aaa' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Process button */}
            {files.length > 0 && (
              <button
                onClick={handleProcess}
                className="w-full mt-4 flex items-center justify-center gap-2 transition-all hover:opacity-90"
                style={{ height: 40, borderRadius: 10, background: '#111', color: '#fff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                data-testid="lens-process-btn"
              >
                <Camera style={{ width: 14, height: 14 }} />
                Process {files.length > 1 ? `${files.length} documents` : 'document'}
                <ArrowRight style={{ width: 14, height: 14, opacity: 0.6 }} />
              </button>
            )}
          </div>
        )}

        <style>{`
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
          @keyframes ping { 75%, 100% { transform: scale(1.5); opacity: 0; } }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default LensUploadLightbox;
